/**
 * OMON-308: the trajectory chart's x-axis window — pure date arithmetic, no
 * Chart.js import.
 *
 * Split from trajectory-chart.ts for the same reason chart-sync.ts is split
 * from chart-kit.ts and monitored-chart-data.ts from monitored-chart.ts: the
 * Chart.js zoom plugin pulls in hammerjs, which needs a DOM, so anything
 * importing it can't be unit-tested in node.
 *
 * The thing this module exists to keep straight is that a chart now has TWO
 * ranges, not one:
 *
 *   - the WINDOW  — what it opens on, and what resetZoom() returns to
 *   - the BOUNDS  — how far pan and zoom-out may reach
 *
 * They used to be the same pair of numbers (planStart..planEnd), which is why
 * the right-hand edge of every swimlane was the end of the plan: on a
 * 12-month plan with two months of recordings, most of the width was empty
 * future and the readings were crushed into the left margin.
 */

import { addMonths } from "date-fns";
import type { ActualObservation } from "../types";

/** How far either side of today the read view opens. */
export const TRAJECTORY_VIEW_MONTHS = 1;

export interface TrajectoryWindow {
  min: number;
  max: number;
}

/**
 * Everything the chart is allowed to reach by panning or zooming out: the plan
 * window, every actual observation (a recording can post-date planEnd), and
 * today itself.
 *
 * Kept separate from the window because the zoom plugin's `limits` is a prison
 * as much as a backstop — passing the narrowed window here would leave the rest
 * of the plan unreachable, which is the opposite of what narrowing it is for.
 *
 * Today is folded in so that it stays reachable on a plan that ended months
 * ago; without that, the window below could not be centred on it.
 */
export function trajectoryBounds(
  planStart: Date,
  planEnd: Date,
  actuals: ActualObservation[],
  now: number = Date.now(),
): TrajectoryWindow {
  let min = planStart.getTime();
  let max = planEnd.getTime();
  for (const obs of actuals) {
    const t = new Date(obs.date).getTime();
    if (!Number.isFinite(t)) continue;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  const today = new Date(now);
  min = Math.min(min, addMonths(today, -TRAJECTORY_VIEW_MONTHS).getTime());
  max = Math.max(max, addMonths(today, TRAJECTORY_VIEW_MONTHS).getTime());
  return { min, max };
}

/**
 * The default read view: today ± TRAJECTORY_VIEW_MONTHS, shifted (never
 * resized) if that would land entirely off the data.
 *
 * The shift matters for a plan that has ended: today ± a month would show an
 * empty chart and read as broken, when the honest thing to show is the tail of
 * the plan. When today sits inside `bounds` — the ordinary live-plan case —
 * nothing is shifted and today is dead centre, which is the whole point.
 */
/**
 * OMON-371: never open on an empty month when the patient has scores. If the
 * most recent score (across ALL the patient's goals: the charts are synced, so
 * they share one window) is older than the window's start, pull the start back
 * to one month before that score. The end is untouched, so today stays in view.
 *
 * Found live (Ed, 2026-10-09, at a conference): a demo client's scores ended a
 * month before "today", the chart opened on today ± a month, and every goal
 * looked wiped. Pinch-to-zoom reached the data; nobody found it.
 */
export function windowReachingData(
  window: TrajectoryWindow,
  latestActualMs: number | undefined,
): TrajectoryWindow {
  if (latestActualMs === undefined || !Number.isFinite(latestActualMs)) return window;
  if (latestActualMs >= window.min) return window;
  return { min: addMonths(new Date(latestActualMs), -TRAJECTORY_VIEW_MONTHS).getTime(), max: window.max };
}

/** The newest score across every goal's actuals (ms), or undefined if none. */
export function latestActualMs(actualsByGoal: Record<string, ActualObservation[]>): number | undefined {
  let latest: number | undefined;
  for (const list of Object.values(actualsByGoal)) {
    for (const obs of list || []) {
      const t = new Date(obs.date).getTime();
      if (Number.isFinite(t) && (latest === undefined || t > latest)) latest = t;
    }
  }
  return latest;
}

export function todayCentredWindow(bounds: TrajectoryWindow, now: number = Date.now()): TrajectoryWindow {
  const today = new Date(now);
  let min = addMonths(today, -TRAJECTORY_VIEW_MONTHS).getTime();
  let max = addMonths(today, TRAJECTORY_VIEW_MONTHS).getTime();
  const span = max - min;
  // Wider than everything there is to show? Then just show everything.
  if (span >= bounds.max - bounds.min) return { min: bounds.min, max: bounds.max };
  if (max < bounds.min) {
    min = bounds.min;
    max = bounds.min + span;
  } else if (min > bounds.max) {
    max = bounds.max;
    min = bounds.max - span;
  }
  return { min, max };
}
